import React, { useEffect, useRef } from 'react';
import { Button, Dropdown } from 'react-bootstrap';

import { useControlledDropdown } from '@/lomavisSharedUi/hooks/useControlledDropdown';
import {
  getOverlayTemplateFolderId,
  getOverlayTemplateFolderLibrary,
  getOverlayTemplateFolderSelection,
  OverlayTemplateFolder,
  OverlayTemplateFolderLibrary,
  OverlayTemplateLibrarySelection
} from '@/lomavisSharedUi/types/overlayTemplates';

const BOOTSTRAP_LARGE_BREAKPOINT_PX = 992;

export interface LomavisTemplateFolderSidebarLabels {
  navigation: string;
  title: string;
  allTemplates: string;
  sharedTemplates?: string;
  myTemplates?: string;
  newFolder: string;
  folderActions: string;
  renameFolder: string;
  deleteFolder: string;
}

export interface LomavisTemplateFolderSidebarProps {
  folders: OverlayTemplateFolder[];
  selection?: OverlayTemplateLibrarySelection;
  selectedFolderId?: string | null;
  labels: LomavisTemplateFolderSidebarLabels;
  showAllCategory?: boolean;
  showSharedCategory?: boolean;
  showMineCategory?: boolean;
  folderLibrary?: OverlayTemplateFolderLibrary;
  canManageFolders?: boolean;
  isLoading?: boolean;
  hasMoreFolders?: boolean;
  isLoadingMoreFolders?: boolean;
  errorMessage?: string | null;
  loadMoreErrorMessage?: string | null;
  loadingLabel?: string;
  retryLabel?: string;
  onSelectionChange?: (selection: OverlayTemplateLibrarySelection) => void;
  onFolderSelect?: (folderId: string | null) => void;
  onCreateFolder?: () => void;
  onRenameFolder?: (folder: OverlayTemplateFolder) => void;
  onDeleteFolder?: (folder: OverlayTemplateFolder) => void;
  onLoadMoreFolders?: () => void;
  onRetryFolders?: () => void;
  onRetryLoadMoreFolders?: () => void;
}

const FolderActionsDropdown: React.FC<{
  folder: OverlayTemplateFolder;
  isSelected: boolean;
  labels: LomavisTemplateFolderSidebarLabels;
  onRenameFolder?: (folder: OverlayTemplateFolder) => void;
  onDeleteFolder?: (folder: OverlayTemplateFolder) => void;
}> = ({ folder, isSelected, labels, onRenameFolder, onDeleteFolder }) => {
  const { close, containerRef, show, toggle } = useControlledDropdown();

  return (
    <div ref={containerRef} className="flex-shrink-0 me-2">
      <Dropdown align="end" show={show} onToggle={() => {}}>
        <Dropdown.Toggle
          type="button"
          size="sm"
          bsPrefix="btn"
          variant="link"
          className={isSelected ? 'text-primary' : 'text-muted'}
          aria-label={`${labels.folderActions}: ${folder.name}`}
          onClick={toggle}
        >
          <i className="fa-regular fa-ellipsis-vertical" aria-hidden="true" />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {onRenameFolder && (
            <Dropdown.Item
              as="button"
              aria-label={`${labels.renameFolder}: ${folder.name}`}
              onClick={() => {
                close();
                onRenameFolder(folder);
              }}
            >
              {labels.renameFolder}
            </Dropdown.Item>
          )}
          {onDeleteFolder && (
            <Dropdown.Item
              as="button"
              className="text-danger"
              aria-label={`${labels.deleteFolder}: ${folder.name}`}
              onClick={() => {
                close();
                onDeleteFolder(folder);
              }}
            >
              {labels.deleteFolder}
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

/**
 * Organism: one-level folder navigation for an overlay-template library.
 */
export const LomavisTemplateFolderSidebar: React.FC<
  LomavisTemplateFolderSidebarProps
> = ({
  folders,
  selection,
  selectedFolderId,
  labels,
  showAllCategory = true,
  showSharedCategory = false,
  showMineCategory = false,
  folderLibrary,
  canManageFolders = false,
  isLoading = false,
  hasMoreFolders = false,
  isLoadingMoreFolders = false,
  errorMessage,
  loadMoreErrorMessage,
  loadingLabel = 'Loading folders',
  retryLabel = 'Retry',
  onSelectionChange,
  onFolderSelect,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onLoadMoreFolders,
  onRetryFolders,
  onRetryLoadMoreFolders
}) => {
  const sidebarRef = useRef<HTMLElement | null>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const isLoadingMoreFoldersRef = useRef(isLoadingMoreFolders);
  const onLoadMoreFoldersRef = useRef(onLoadMoreFolders);
  const autoLoadRequestedRef = useRef(false);
  const canLoadMoreFolders = Boolean(onLoadMoreFolders);
  const selectedClasses = 'bg-light-primary text-primary fw-semibold';
  const defaultClasses = 'text-dark';
  const activeSelection =
    selection ??
    (selectedFolderId
      ? folderLibrary
        ? getOverlayTemplateFolderSelection(folderLibrary, selectedFolderId)
        : `folder:${selectedFolderId}`
      : showAllCategory
      ? 'all'
      : showSharedCategory
      ? 'shared'
      : 'mine');
  const activeFolderLibrary =
    getOverlayTemplateFolderLibrary(activeSelection) ??
    (activeSelection.startsWith('folder:') ? folderLibrary : undefined);
  const select = (nextSelection: OverlayTemplateLibrarySelection) => {
    onSelectionChange?.(nextSelection);
    if (onFolderSelect) {
      onFolderSelect(getOverlayTemplateFolderId(nextSelection));
    }
  };

  useEffect(() => {
    isLoadingMoreFoldersRef.current = isLoadingMoreFolders;
    onLoadMoreFoldersRef.current = onLoadMoreFolders;
  }, [isLoadingMoreFolders, onLoadMoreFolders]);

  useEffect(() => {
    if (isLoading) autoLoadRequestedRef.current = false;
  }, [isLoading]);

  const categoryButton = (
    category: 'all' | 'shared' | 'mine',
    iconClassName: string,
    label: string
  ) => {
    const isSelected = activeSelection === category;
    return (
      <button
        type="button"
        className={`btn btn-sm text-start py-2 px-3 ${
          isSelected ? selectedClasses : defaultClasses
        }`}
        aria-current={isSelected ? 'page' : undefined}
        onClick={() => select(category)}
      >
        <i className={`${iconClassName} me-2`} aria-hidden="true" />
        {label}
      </button>
    );
  };

  const renderFolders = (library?: OverlayTemplateFolderLibrary) => {
    const categoryIsActive = library
      ? activeSelection === library || activeFolderLibrary === library
      : activeSelection === 'all' || activeSelection.startsWith('folder:');
    if (folderLibrary !== library || !categoryIsActive) return null;

    return (
      <div className="d-flex flex-column gap-1 ms-3">
        {isLoading && (
          <div
            className="d-flex align-items-center justify-content-center gap-2 px-3 py-3 text-muted"
            role="status"
            aria-label={loadingLabel}
          >
            <span
              className="spinner-border spinner-border-sm text-secondary"
              aria-hidden="true"
            />
            <span className="small">{loadingLabel}</span>
          </div>
        )}

        {!isLoading && errorMessage && (
          <div className="small text-danger px-3 py-2" role="alert">
            <p className="mb-2">{errorMessage}</p>
            {onRetryFolders && (
              <Button
                type="button"
                size="sm"
                variant="outline-danger"
                onClick={onRetryFolders}
              >
                {retryLabel}
              </Button>
            )}
          </div>
        )}

        {!isLoading &&
          !errorMessage &&
          folders.map((folder) => {
            const folderSelection = library
              ? getOverlayTemplateFolderSelection(library, folder.id)
              : (`folder:${folder.id}` as OverlayTemplateLibrarySelection);
            const isSelected = activeSelection === folderSelection;

            return (
              <div
                key={folder.id}
                className={`d-flex align-items-center rounded ${
                  isSelected ? selectedClasses : defaultClasses
                }`}
              >
                <button
                  type="button"
                  className={`btn btn-sm border-0 d-flex align-items-center text-start flex-grow-1 overflow-hidden py-2 ps-3 pe-2 ${
                    isSelected ? 'text-primary fw-semibold' : 'text-dark'
                  }`}
                  aria-current={isSelected ? 'page' : undefined}
                  title={folder.name}
                  onClick={() => select(folderSelection)}
                >
                  <i
                    className="fa-regular fa-folder flex-shrink-0 me-2"
                    aria-hidden="true"
                  />
                  <span className="d-block text-truncate">{folder.name}</span>
                </button>

                {canManageFolders && (onRenameFolder || onDeleteFolder) && (
                  <FolderActionsDropdown
                    folder={folder}
                    isSelected={isSelected}
                    labels={labels}
                    onRenameFolder={onRenameFolder}
                    onDeleteFolder={onDeleteFolder}
                  />
                )}
              </div>
            );
          })}

        {!isLoading && !errorMessage && (
          <div
            ref={loadMoreSentinelRef}
            className="d-flex align-items-center justify-content-center px-3 py-3"
          >
            {isLoadingMoreFolders && (
              <>
                <span
                  className="spinner-border spinner-border-sm text-secondary me-2"
                  aria-hidden="true"
                />
                <span className="small text-muted">{loadingLabel}</span>
              </>
            )}
            {!isLoadingMoreFolders && loadMoreErrorMessage && (
              <div className="small text-danger text-center" role="alert">
                <p className="mb-2">{loadMoreErrorMessage}</p>
                {onRetryLoadMoreFolders && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline-danger"
                    onClick={onRetryLoadMoreFolders}
                  >
                    {retryLabel}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    const root = sidebarRef.current;
    const target = loadMoreSentinelRef.current;
    if (
      !root ||
      !target ||
      !hasMoreFolders ||
      isLoading ||
      errorMessage ||
      loadMoreErrorMessage ||
      !canLoadMoreFolders ||
      typeof IntersectionObserver === 'undefined'
    )
      return;

    const createObserver = () => {
      const parentRect = root.parentElement?.getBoundingClientRect();
      const rootRect = root.getBoundingClientRect();
      const isDesktopViewport =
        window.innerWidth >= BOOTSTRAP_LARGE_BREAKPOINT_PX;
      const isBoundedByParent =
        !parentRect?.height || rootRect.height <= parentRect.height + 1;
      const observerRoot = isDesktopViewport && isBoundedByParent ? root : null;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) {
            autoLoadRequestedRef.current = false;
            return;
          }
          if (autoLoadRequestedRef.current || isLoadingMoreFoldersRef.current)
            return;
          autoLoadRequestedRef.current = true;
          onLoadMoreFoldersRef.current?.();
        },
        { root: observerRoot, rootMargin: '160px 0px', threshold: 0.01 }
      );
      observer.observe(target);
      return observer;
    };

    let observer = createObserver();
    const handleResize = () => {
      observer.disconnect();
      observer = createObserver();
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, [
    errorMessage,
    hasMoreFolders,
    isLoading,
    loadMoreErrorMessage,
    canLoadMoreFolders,
    activeSelection,
    folderLibrary
  ]);

  return (
    <aside
      ref={sidebarRef}
      className="h-100 bg-light overflow-auto"
      aria-label={labels.navigation}
    >
      <div className="d-flex flex-column gap-3 p-4 pb-3">
        <h2 className="text-uppercase text-muted fw-bold fs-8 mb-0">
          {labels.title}
        </h2>
        {canManageFolders && onCreateFolder && (
          <Button
            type="button"
            size="sm"
            variant="outline-secondary"
            className="w-100 d-flex align-items-center justify-content-center gap-2"
            onClick={onCreateFolder}
          >
            <i className="fa-regular fa-plus" aria-hidden="true" />
            {labels.newFolder}
          </Button>
        )}
      </div>

      <nav
        className="d-flex flex-column gap-1 px-3 pb-4"
        aria-label={labels.navigation}
      >
        {showAllCategory &&
          categoryButton('all', 'fa-regular fa-grid-2', labels.allTemplates)}
        {showSharedCategory && (
          <>
            {categoryButton(
              'shared',
              'fa-regular fa-building',
              labels.sharedTemplates ?? 'Shared overlays'
            )}
            {renderFolders('shared')}
          </>
        )}
        {showMineCategory && (
          <>
            {categoryButton(
              'mine',
              'fa-regular fa-user',
              labels.myTemplates ?? 'My overlays'
            )}
            {renderFolders('mine')}
          </>
        )}
        {!folderLibrary && renderFolders()}
      </nav>
    </aside>
  );
};
